<template>
  <div class="detail-charts">
    <div class="chart-panel">
      <transition name="fade">
        <div v-if="loading" class="loading-wrapper">
          <BaseSpinner text="info.loading_data" />
        </div>
      </transition>
      <div class="chart-panel__header">
        <div>{{ $t(`metrics.${sensorType.name.toLowerCase()}`) }}</div>
        <div class="chart-panel__info__period" v-click-outside="hideChartPeriodSelect">
          <div @click="toggleChartPeriodSelect" style="display: flex;">
            <span v-responsive.sm.md.lg.xl>{{ $t('detail.select_data') }}:</span>
            <span>{{ computedPeriodOption.name }}</span>
            <BaseIcon icon="chevron-down" />
          </div>
          <transition name="fadeUp">
            <BaseSelectList
                    v-if="showChartPeriodSelect"
                    :items="chartPeriodOptions"
                    :active="computedPeriodOption"
                    @onSelect="setChartPeriod"
                    style="z-index: 13;"
            />
          </transition>
        </div>
      </div>
      <div class="chart-panel__chart">
        <apexchart height="100%" width="100%" type="line" :options="chartOptions" :series="series"></apexchart>
      </div>
    </div>
  </div>
</template>
<script>
    import VueApexCharts from 'vue-apexcharts';
    import { mapGetters } from 'vuex';
    import { chartPeriods } from '@/config/charts';

    export default {
        name: 'DetailChartPanel',
        components: {
            apexchart: VueApexCharts,
        },
        props: {
            sensorType: {
                type: Object,
                required: true,
            },
            unit: {
                type: Object,
                required: true,
            },
            loadedData: {
                type: Array,
                default: [],
            },
        },
        created() {
            // this.setChartData(this.getDateFromToday(-1, 0, 0), this.getDateFromToday(0, 0, 0));
            // this.transformData(this.loadedData);
            if (!this.sensorType.type) {
                this.loading = false;
            }
        },
        data() {
            return {
                // START CHART OPTION
                monthNames: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                colors: ['#b2de4d', '#64b5f6', '#ffb74d', '#f47f7f',
                    '#a1887f', '#7da453', '#2286c3', '#c75b39',
                    '#af4448', '#725b53'],
                loading: true,
                series: [],
                // END CHART OPTIONS
                showChartSettings: false,
                showChartPeriodSelect: false,
                selectedPeriod: { id: 2, value: 'month', name: this.$t('interval.month') },
            };
        },
        computed: {
            ...mapGetters('tentacle', ['timeFormat']),
            computedPeriodOption() {
                const option = this.selectedPeriod;
                option.name = this.$t(`interval.${option.value}`);
                return option;
            },
            chartPeriodOptions() {
                chartPeriods.forEach(item => item.name = this.$t(`interval.${item.value}`));
                return chartPeriods;
            },
            chartOptions() {
                return {
                    chart: {
                        height: 350,
                        foreColor: '#999',
                        scroller: {
                            enabled: true,
                            track: {
                                height: 7,
                                background: '#e0e0e0',
                            },
                            thumb: {
                                height: 10,
                                background: '#94E3FF',
                            },
                            scrollButtons: {
                                enabled: true,
                                size: 9,
                                borderWidth: 2,
                                borderColor: '#008FFB',
                            },
                            padding: {
                                left: 30,
                                right: 20,
                            },
                        },
                    },
                    colors: this.colors,
                    stroke: {
                        curve: 'smooth',
                        width: 3,
                    },
                    dataLabels: {
                        enabled: false,
                    },
                    markers: {
                        size: 0,
                        strokeColor: '#fff',
                        strokeWidth: 3,
                        strokeOpacity: 1,
                        fillOpacity: 1,
                        hover: {
                            size: 6,
                        },
                    },
                    yaxis: {
                        decimalsInFloat: 2,
                        labels: {
                            show: true,
                            formatter: val => val,
                            style: {
                                color: '#acb4c5',
                                fontSize: '12px',
                                fontFamily: 'Rubik, Helvetica, Arial, sans-serif',
                            },
                        },
                    },
                    xaxis: {
                        type: 'datetime',
                        labels: {
                            // format: 'dd MMM yyyy',
                            style: {
                                colors: '#acb4c5',
                                fontSize: '12px',
                                fontFamily: 'Rubik, Helvetica, Arial, sans-serif',
                            },
                        },
                    },
                    grid: {
                        show: true,
                        borderColor: '#eee',
                        strokeDashArray: 5,
                        position: 'back',
                        xaxis: {
                            lines: {
                                show: true,
                            },
                        },
                        yaxis: {
                            lines: {
                                show: true,
                            },
                        },
                    },
                    tooltip: {
                        enabled: true,
                        custom: ({
                                     series, seriesIndex, dataPointIndex, w,
                                 }) => {
                            const newDate = new Date(Number(this.series[seriesIndex].data[dataPointIndex][0]));
                            const outDate = this.moment(newDate).format(this.timeFormat ? 'dddd, DD MMM YYYY, hh:mm a' : 'dddd, DD MMM YYYY, HH:mm');
                            return `<div style="font-size:0.9em; font-family: Arial;width: 20em;height: 7em;background:white !important;">
                      <div style="width:20em; background:${this.colors[seriesIndex]}25;">
                        <div style="padding:0.5em;">${outDate}</div>
                      </div>
                      <div style="padding: 1em;padding-bottom: 0.5em;">
                        <span style="display: inline-block;width: 12px;height: 12px;position: relative;top: 2px;margin-right: 10px;border-radius: 50%;background-color:${this.colors[seriesIndex]};"></span>
                        ${this.series[seriesIndex].name}:
                        <strong style="font-size: 1.15em;">${series[seriesIndex][dataPointIndex].toFixed(2)} ${this.sensorType.unit}</strong>
                      </div>
                      <div style="margin-left: 9px;align-items: center;display: inline-flex;">
                        <svg width="20" height="20" style="fill: black;margin-top:-2px; margin-right:10px;">
                          <use href="/svg-sprite.svg#location" />
                        </svg>
                        ${this.series[seriesIndex].remark}
                      </div>
                    </div>`;
                        },
                        style: {
                            fontSize: '12px',
                            fontFamily: 'Rubik, Helvetica, Arial, sans-serif',
                        },
                        x: {
                            formatter: (val, opts) => {
                                const newDate = new Date(val);
                                const outDate = this.moment(newDate).format(this.timeFormat ? 'DD MMM YYYY, hh:mm a' : 'DD MMM YYYY, HH:mm');
                                return `${outDate}`;
                            },
                        },
                        y: {
                            formatter: (val, opts) => `${val} ${this.sensorType.unit}`,
                        },
                        marker: {
                            show: true,
                        },
                    },
                    legend: {
                        show: true,
                        showForSingleSeries: true,
                        fontSize: '14px',
                        fontFamily: 'Rubik, Helvetica, Arial, sans-serif',
                        offsetY: -10,
                        labels: {
                            colors: '#acb4c5',
                        },
                        itemMargin: {
                            horizontal: 10,
                            vertical: 10,
                        },
                    },
                    noData: {
                        text: this.$t('detail.missing'),
                        align: 'center',
                        verticalAlign: 'middle',
                        offsetX: 0,
                        offsetY: 0,
                        style: {
                            color: undefined,
                            fontSize: '14px',
                            fontFamily: 'Rubik, Helvetica, Arial, sans-serif',
                        },
                    },
                };
            },
        },
        watch: {
            loadedData(newVal, oldVal) {
                this.updateData(newVal);
            },
        },
        methods: {
            updateData(newData) {
                const filtered = newData.filter(item => this.sensorType.id.includes(item.id));
                if (filtered.length > 0) {
                    this.loading = true;
                    const series = [];
                    filtered.forEach((data) => {
                        if (data.id) {
                            const tent = this.$store.getters['tentacle/unitBySensor'](data.id);
                            const sensor = {
                                name: tent.name,
                                remark: tent.remark,
                                data: [],
                            };
                            sensor.data = data.values.reduce((total, current) => {
                                total.push([Number(current.timestamp) * 1000, current.value]);
                                return total;
                            }, []);
                            series.push(sensor);
                        }
                    });
                    this.series = series;
                    this.loading = false;
                }
            },
            getDateFromToday(year, month, day) {
                return `${new Date().getFullYear() + year}-${new Date().getMonth() + 1 + month}-${new Date().getDate() + day}`;
            },
            setChartData(fromDate, toDate) {
                this.loading = true;
                const filter = this.sensorType.id.reduce((total, current) => {
                    total.push({ id: current, from: fromDate, to: toDate });
                    return total;
                }, []);
                const data = {
                  filter: filter,
                  tentacle: this.unit.id
                };
                this.$store.dispatch('tentacle/setMeasurement', data).then((resolve) => {
                    this.updateData(resolve.data ? resolve.data : []);
                });
            },
            toggleChartSettings() {
                this.showChartSettings = !this.showChartSettings;
            },
            toggleChartPeriodSelect() {
                this.showChartPeriodSelect = !this.showChartPeriodSelect;
            },
            hideChartPeriodSelect() {
                this.showChartPeriodSelect = false;
            },
            setChartPeriod(item) {
                this.showChartPeriodSelect = !this.showChartPeriodSelect;
                if (item.value === 'year' && this.selectedPeriod.value !== 'year') {
                    this.setChartData(this.getDateFromToday(-1, 0, 0), this.getDateFromToday(0, 0, 0));
                } else if (item.value === 'month' && this.selectedPeriod.value !== 'month') {
                    this.setChartData(this.getDateFromToday(0, -1, 0), this.getDateFromToday(0, 0, 0));
                } else if (item.value === 'week' && this.selectedPeriod.value !== 'week') {
                    this.setChartData(this.getDateFromToday(0, 0, -7), this.getDateFromToday(0, 0, 0));
                } else if (item.value === 'day' && this.selectedPeriod.value !== 'day') {
                    this.setChartData(this.getDateFromToday(0, 0, -1), this.getDateFromToday(0, 0, 0));
                }
                this.selectedPeriod = item;
            },
        },
    };
</script>

<style lang="scss" scoped>
  .chart-panel {
    position:relative;
    margin-bottom: 4rem;
    border-radius: 2rem;
    background-color: #fff;
    box-shadow: 0 0 5px rgba(100, 116, 160, 0.1);
    @media only screen and (max-width: 1199px) {
      width: 90%;
      margin:auto;
      margin-top: 2em;
    }

    &__header {
      padding: 0 3rem;
      height: 6rem;
      border-bottom: 0.1rem solid #eee;
      display: flex;
      align-items: center;
      font-weight: 600;
      font-size: 1.4rem;
      color: $grey;

      &__settings {
        position: relative;
        margin-left: auto;
        cursor: pointer;
        display: flex;
        align-items: center;

        svg {
          width: 1.8rem;
          height: 1.8rem;
        }

        .icon {
          fill: $grey;
          width: 1.8rem 1.8rem;
        }

        .graph-settings {
          position: absolute;
          z-index: 12;
          top: 3rem;
          right: -1rem;
          padding: 1rem;
          width: 20rem;
          height: 10rem;
          background-color: white;
          box-shadow: 0 0.5rem 1.5rem 0 rgba(37, 44, 97, 0.15),
          0 0.2rem 0.4rem 0 rgba(93, 100, 148, 0.2);
        }
      }
    }

    &__info {
      padding: 0 3rem;
      display: flex;
      height: 8rem;
      align-items: stretch;

      &__min,
      &__actual,
      &__max {
        min-width: 18rem;
        display: flex;
        height: 100%;
        align-items: center;
        font-size: 3rem;
        font-weight: 700;
        color: $dark;

        .value {
          color: $grey;

          &.max {
            color: $max-color;
          }

          &.min {
            color: $min-color;
          }
        }

        .description {
          font-size: 1.1rem;
          width: 4.6rem;
          display: inline-flex;
          align-items: center;
          height: 3rem;
          text-align: end;
          margin-right: 2rem;
          position: relative;

          &:after {
            content: '';
            display: inline-block;
            position: absolute;
            right: -1rem;
            width: 0.1rem;
            height: 100%;
            background-color: #ececec;
          }
        }
      }

      &__actual {
        .value {
          color: $company-color;
        }
      }

      &__period {
        position: relative;
        display: flex;
        align-items: center;
        margin-left: auto;
        font-weight: 500;
        color: $grey;
        align-self: center;
        padding: 1.5rem;
        border-radius: 0.5rem;
        cursor: pointer;
        font-size: 1.2rem;
        transition: all 0.4s;

        svg {
          width: 1.2rem;
          height: 1.2rem;
        }

        &:hover {
          background-color: #f5f5f5;
        }

        span {
          margin-left: 1rem;
          min-width: 7rem;
          color: $dark;
        }
      }
    }

    &__chart {
      padding: 0 3rem;
      margin-top: 3em;
      min-height: 36rem;
      width: 100%;
      height: 280px;
      @media only screen and (max-width: 991px) {
        padding: 0;
      }
    }

    &__footer {
      height: 6rem;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.2rem;
      font-weight: 500;
      color: $grey;

      span {
        width: 2rem;
        height: 0.6rem;
        border-radius: 0.2rem;
        background-color: $company-color;
        margin-right: 0.5rem;
      }
    }
  }
  .loading-wrapper {
    position: absolute;
    border-radius: 2rem;
    top:0;
    left:0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
  }
</style>
